import { useMemo, useState } from 'react'
import { Alert, Button, Card, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { LockOutlined, PlusOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { usePermissions } from '@/shared/auth/usePermissions'
import { EmptyState } from '@/shared/ui/EmptyState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RowActions } from '@/shared/ui/table/RowActions'
import { AccountFormModal } from '@/features/finance/AccountFormModal'
import { ReportFilterBar } from '@/features/finance/ReportFilterBar'
import { ReportTableEmpty } from '@/features/finance/ReportTableEmpty'
import { matchesSearch, matchesValues, useLocalListState } from '@/shared/lib/localList'
import {
  ACCOUNT_TYPES,
  buildAccountTree,
  fetchAccounts,
  type Account,
  type AccountTreeNode,
} from '@/features/finance/financeApi'

/**
 * Hisob turining rangi.
 *
 * ⚠️ Noma'lum tur uchun rang YO'Q (`undefined`) va bu ataylab: server
 * yangi tur qo'shsa qator baribir chiziladi, faqat bo'yalmaydi. Xarita
 * ichida `??` bilan boshqa rangga tushirish "bu daromad" degan YOLG'ON
 * xabar berardi.
 *
 * ⚠️ Xarajat QIZIL EMAS: qizil bu panelda NOSOZLIK degani (`EmptyState`
 * dagi bir xil qoida — rang o'z ma'nosini faqat kam ishlatilganda
 * saqlaydi). Xarajat esa odatiy hol, xato emas.
 */
const TYPE_COLOR: Readonly<Record<string, string>> = {
  ASSET: 'blue',
  LIABILITY: 'orange',
  EQUITY: 'purple',
  REVENUE: 'green',
  EXPENSE: 'gold',
}

/** URL'dagi holat filtri qiymatlari — `active` maydonining ikki holati */
const STATE_OPEN = 'OPEN'
const STATE_CLOSED = 'CLOSED'

/**
 * Ro'yxatni TORAYTIRADIGAN filtrlar — holat bu yerda YO'Q va bu ataylab.
 *
 * ⚠️ Holat bu sahifada KONTEKST: u «qaysi rejani ko'ryapman» degan
 * savolga javob beradi, qatorlarni toraytirmaydi. Uni toraytiruvchi deb
 * sanash ikki narsani buzardi: «Tozalash» tugmasi DOIM turardi (sukut
 * qiymatning o'zi filtr bo'lib ko'rinardi), va yopilganlarni ochgan
 * operator tozalash tugmasini bosganda ular JIMGINA yana yashirilardi.
 */
const NARROWING_KEYS = ['type'] as const

/**
 * Hisoblar rejasi — `xsib-finance` ning ma'lumotnomasi.
 *
 * <h2>⚠️ REJA SOZLANADIGAN (docs/10-MIKROSERVIS.md §13.6)</h2>
 * Migratsiyadagi boshlang'ich ro'yxat — MA'LUMOT, qaror emas. Operator
 * o'z rejasini quradi: hisob qo'shadi, nomini o'zgartiradi,
 * ishlatilmaydiganini yopadi.
 *
 * <h2>⚠️ SAHIFALASH YO'Q — ENDPOINT UNI QO'LLAMAYDI</h2>
 * `GET /api/finance/accounts` faqat `activeOnly` ni oladi
 * (`AccountController#list`) — `page`, `size`, `sort`, `q` YO'Q.
 * Javob har doim to'liq keladi, ya'ni bu yerda sahifalash tugmalarini
 * chizish «butun ro'yxatni olib, uida kesish» naqshining o'zi bo'lardi.
 * Qidiruv va filtr esa BUTUN to'plamga qo'llanadi (sahifa tushunchasi
 * yo'q), shuning uchun natija to'liq va yolg'on tartib bo'lishi mumkin
 * emas — `shared/lib/localList.ts` dagi batafsil izoh.
 *
 * <h2>⚠️ `created desc` QO'LLANMAYDI — SERVER SANANI BERMAYDI</h2>
 * `AccountView` da `createdAt` maydoni UMUMAN yo'q (`accountSchema`).
 * Uning o'rniga tabiiy tartib — REJA tartibi (`parent_id` daraxti,
 * ichida hisob KODI bo'yicha). Buxgalter rejani aynan shu tartibda
 * o'qiydi va "oxirgi qo'shilgan birinchi" uni o'qib bo'lmaydigan
 * qilardi.
 *
 * <h2>⚠️ O'CHIRISH TUGMASI YO'Q — SERVERDA ham yo'q</h2>
 * `DELETE` yo'li ochilmagan: hisobga provodka yozilgan bo'lishi mumkin,
 * provodka esa O'ZGARMAS. O'chirish jurnalni "hisobi noma'lum satrlar"
 * bilan qoldirardi. Ishlatilmaydigan hisob `active = false` bilan
 * yopiladi.
 *
 * <h2>⚠️ DARAXT — `parent_id` BO'YICHA, CHUQURLIK CHEKLANMAGAN</h2>
 * Bu TASHKILIY daraxt emas (u `platform` da va u yerda `path` bor) —
 * hisoblar rejasining o'z iyerarxiyasi, `finance.account` ichida.
 * §20.4 dagi "daraxt bitta egada" qoidasi tashkiliy daraxtga tegishli
 * va bu yerga aloqasi yo'q.
 *
 * <h2>⚠️ SUKUT BO'YICHA FAQAT OCHIQ HISOBLAR (egasi qarori 2026-08-20)</h2>
 * «Yopilganlar qoidasi»: yopilgan hisob rejada ko'rinmaydi va u HOLAT
 * tanlagichi orqali olinadi. Tanlagichda bo'sh holat yo'q — u `OPEN`
 * dan boshlanadi.
 *
 * <h2>⚠️ NEGA `activeOnly` SERVERGA YUBORILMAYDI</h2>
 * Yagona so'rov BUTUN rejani oladi va sabab ko'rinishda emas, YOZISHDA:
 * `AccountFormModal` ota tanlagichi va SIKL qorovuli
 * (`collectAccountSubtree`) to'liq to'plamsiz ishlay olmaydi — yopilgan
 * ota ro'yxatdan tushib qolsa, mavjud hisobni tahrirlashda «otasi yo'q»
 * degan YOLG'ON ko'rinardi va tahrir uni jimgina ildizga ko'chirardi.
 * Ikkinchi so'rov yuborish o'rniga yagona to'liq javob olinadi va
 * KO'RINISH shu ustida chegaralanadi.
 *
 * <p>⚠️ Bu «soxta filtr» EMAS: endpoint sahifalanmaydi
 * (`AccountController#list` da `page`/`size` yo'q), ya'ni massivda butun
 * reja turibdi va «faqat joriy sahifa filtrlandi» holatining O'ZI yo'q.
 *
 * <h2>⚠️ YOPILGAN OTA — BOLASI YO'QOLMAYDI, ILDIZGA CHIQADI</h2>
 * Yopilgan guruh hisobning ochiq bolasi bor bo'lishi mumkin.
 * `buildAccountTree` otasi to'plamda bo'lmagan hisobni ILDIZ qilib
 * qaytaradi (uning o'z javadoc'i aynan shu holatni yozadi), ya'ni ochiq
 * bola HECH QACHON ekrandan tushmaydi. Lekin u joyini o'zgartiradi —
 * shuning uchun bunday qatorga «otasi yopilgan» yorlig'i qo'yiladi:
 * hisob nega yuqori darajada turganini operator ekranning o'zida
 * o'qiydi, aks holda reja jimgina boshqa shaklga kirgan bo'lardi.
 */
export function FinanceAccountsPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const filter = useLocalListState({ narrowingKeys: NARROWING_KEYS })

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Account | null>(null)
  const [parentForNew, setParentForNew] = useState<string | null>(null)

  // ⚠️ `activeOnly: false` — javob BUTUN reja. Sabab sinf javadoc'ida:
  // yozish yo'li (ota tanlash va sikl qorovuli) to'liq to'plamsiz
  // ishlamaydi. Ko'rinish esa quyida chegaralanadi.
  const accounts = useQuery({
    queryKey: queryKeys.finance.accounts(false),
    queryFn: ({ signal }) => fetchAccounts(false, signal),
  })

  const rows = useMemo(() => accounts.data ?? [], [accounts.data])
  const canWrite = can('finance.account.write')

  const typeFilter = filter.values['type']
  // ⚠️ Sukut — `OPEN`, `undefined` EMAS: yopilgan hisob rejaning
  //    kundalik ko'rinishida turmaydi (egasi qarori 2026-08-20).
  const stateFilter = filter.values['state']?.[0] ?? STATE_OPEN

  /** Holat tanloviga tushgan hisoblar — daraxt ham, yassi ro'yxat ham SHUNDAN */
  const visible = useMemo(
    () => rows.filter((row) => (stateFilter === STATE_CLOSED ? !row.active : row.active)),
    [rows, stateFilter],
  )

  /**
   * Filtrga tushgan hisoblar — YASSI ro'yxat, kod bo'yicha.
   *
   * ⚠️ Filtr qo'llanganda daraxt SAQLANMAYDI va bu ataylab: mos kelgan
   * bolaning otasi filtrga tushmasligi mumkin, ya'ni daraxt shakli
   * "ota yo'q, bola bor" degan uzuq ko'rinishga aylanardi. Yassi
   * ro'yxatda esa har qator o'z kodi bilan to'liq o'qiladi.
   */
  const matched = useMemo(() => {
    if (!filter.narrowed) return []
    return visible
      .filter(
        (row) =>
          matchesSearch(filter.search, row.code, row.name, seededName('accounts', row.code, row.name, t)) &&
          matchesValues(typeFilter, row.type),
      )
      .slice()
      .sort((a, b) => a.code.localeCompare(b.code))
  }, [visible, filter.narrowed, filter.search, typeFilter, t])

  const tree = useMemo(() => buildAccountTree(visible), [visible])

  /**
   * Otasi ko'rinishdan tushib qolgan hisoblar.
   *
   * ⚠️ Bu ro'yxat YO'QOTISH emas, KO'CHISH belgisi: `buildAccountTree`
   * bunday hisobni ildiz qilib qaytaradi, ya'ni u ekranda qoladi —
   * faqat boshqa joyda. Belgisiz operator rejaning shakli o'zgarganini
   * ko'rmasdi va «bu hisob nega yuqorida?» degan savol javobsiz qolardi.
   */
  const orphanIds = useMemo(() => {
    const present = new Set(visible.map((row) => row.id))
    return new Set(
      visible.filter((row) => row.parentId !== null && !present.has(row.parentId)).map((row) => row.id),
    )
  }, [visible])

  /**
   * ⚠️ `defaultExpandAllRows` BU YERDA ISHLAMAYDI (BUG-025).
   *
   * `rc-table` ochiq kalitlarni `useState(() => ...)` ichida, faqat
   * birinchi renderda hisoblaydi — o'shanda so'rov hali ketayotgan va
   * daraxt bo'sh bo'ladi, ya'ni ro'yxat abadiy bo'sh qoladi. Jonli
   * bazada 29 hisobning 20 tasi bola: ekranda faqat 9 ildiz ko'rinardi.
   *
   * Shuning uchun ochiqlar daraxtdan har safar hisoblanadi, qo'lda
   * yopilganlar esa alohida saqlanadi — yangi qator o'zi ochiq keladi,
   * yopilgani yangilanishda qayta ochilib ketmaydi.
   */
  const branchKeys = useMemo(() => {
    const keys: string[] = []
    const walk = (nodes: readonly AccountTreeNode[]) => {
      for (const node of nodes) {
        if (node.children.length > 0) {
          keys.push(node.id)
          walk(node.children)
        }
      }
    }
    walk(tree)
    return keys
  }, [tree])

  const [collapsed, setCollapsed] = useState<readonly string[]>([])
  const expandedRowKeys = useMemo(
    () => branchKeys.filter((key) => !collapsed.includes(key)),
    [branchKeys, collapsed],
  )

  const openCreate = (parentId: string | null) => {
    setEditing(null)
    setParentForNew(parentId)
    setFormOpen(true)
  }

  /*
    ⚠️ TARTIB RAQAMI USTUNI YO'Q — va bu ataylab.

    Bu DARAXT (`expandable`, `children`): `render` ga keladigan `index`
    faqat JORIY qavatni sanaydi, ya'ni har bola ro'yxati yana 1 dan
    boshlanardi. Jonli bazada 29 hisobning 20 tasi bola — ekranda
    o'nlab «1», «2» qatorlari turardi va raqam operatorni ADASHTIRARDI.

    ⚠️ Filtr qo'llanganda ro'yxat yassilanadi va u yerda raqam TO'G'RI
    bo'lardi — lekin u holda ustun paydo bo'lib, filtr olinganda
    yo'qolardi. «Goh bor, goh yo'q» ustun raqamning O'ZIGA bo'lgan
    ishonchni yo'qotadi, shuning uchun ikkala rejimda ham yo'q.
  */
  const columns: ColumnsType<Account> = [
    {
      key: 'name',
      title: t('finance.account.name'),
      render: (_, row) => (
        <Space size={6} wrap>
          <Typography.Text code>{row.code}</Typography.Text>
          <Typography.Text strong={row.postable === false}>{seededName('accounts', row.code, row.name, t)}</Typography.Text>
          {row.system ? (
            <Tooltip title={t('finance.account.systemBody')}>
              <Tag icon={<LockOutlined />}>{t('finance.account.system')}</Tag>
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    {
      key: 'type',
      title: t('finance.account.type'),
      width: 220,
      render: (_, row) => (
        <Space size={4} wrap>
          <Tag color={TYPE_COLOR[row.type]}>
            {t(`accountType.${row.type}`, { defaultValue: row.type })}
          </Tag>
          {/* ⚠️ Xarajat SINFI — foyda hisoboti aynan shunga qaraydi
              (§19.3), lekin rejada u KO'RINMASDI: operator hisobning
              tannarxmi yoki operatsion xarajatmi ekanini ekranda
              bilolmasdi va noto'g'ri sinf jimgina yalpi foydani
              buzardi. Yorliqlar `FinanceTrialBalancePage` bilan AYNI
              (`expenseClass.*`) — bir tushuncha ikki joyda ikki xil
              nomlanmasin. */}
          {row.expenseClass ? (
            <Tag color="orange">
              {t(`expenseClass.${row.expenseClass}`, { defaultValue: row.expenseClass })}
            </Tag>
          ) : null}
          {row.cashFlow ? (
            <Tooltip title={t('finance.account.cashFlow')}>
              <Tag color="cyan">
                {t(`cashFlowClass.${row.cashFlow}`, { defaultValue: row.cashFlow })}
              </Tag>
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    {
      key: 'flags',
      title: t('finance.account.flags'),
      width: 260,
      render: (_, row) => (
        <Space size={4} wrap>
          {row.postable ? null : (
            <Tooltip title={t('finance.account.groupHint')}>
              <Tag style={{ marginInlineEnd: 0 }}>{t('finance.account.group')}</Tag>
            </Tooltip>
          )}
          {row.requiresDimension ? (
            <Tooltip title={t('finance.account.requiresDimensionHint')}>
              <Tag color="processing" style={{ marginInlineEnd: 0 }}>
                {t('finance.account.requiresDimension')}
              </Tag>
            </Tooltip>
          ) : null}
          {row.active ? null : (
            <Tag color="default" style={{ marginInlineEnd: 0 }}>
              {t('finance.account.closed')}
            </Tag>
          )}
          {/* ⚠️ FAQAT daraxt rejimida: yassi ro'yxatda ota BARIBIR
              ko'rsatilmaydi va yorliq har qatorda takrorlanib ma'nosini
              yo'qotardi. */}
          {!filter.narrowed && orphanIds.has(row.id) ? (
            <Tooltip title={t('finance.account.orphanClosedParentHint')}>
              <Tag color="gold" style={{ marginInlineEnd: 0 }}>
                {t('finance.account.orphanClosedParent')}
              </Tag>
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
  ]

  if (canWrite) {
    columns.push({
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'edit',
              label: t('common.edit'),
              onClick: () => {
                setEditing(row)
                setParentForNew(null)
                setFormOpen(true)
              },
              permission: 'finance.account.write',
            },
            {
              key: 'addChild',
              label: t('finance.account.addChild'),
              onClick: () => openCreate(row.id),
              permission: 'finance.account.write',
              divider: true,
            },
          ]}
        />
      ),
    })
  }

  // ⚠️ «Ko'rsatildi / yuklandi»: `loaded` BUTUN reja bo'lgani uchun
  //    yopilganlar yashiringanda son farq qiladi va bu KERAK — operator
  //    ro'yxat toraytirilganini shu yerdan ko'radi.
  const shown = filter.narrowed ? matched.length : visible.length

  return (
    <>
      <PageHeader
        title={t('nav.financeAccounts')}
        description={t('finance.account.intro')}
        extra={
          <Can permission="finance.account.write">
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate(null)}>
              {t('finance.account.createTitle')}
            </Button>
          </Can>
        }
      />

      {/* ⚠️ Yiqilgan so'rov BO'SH JADVAL bo'lib ko'rinmasin: "reja bo'sh"
          degan xulosa faqat HAQIQATAN o'qilgan javobdan chiqadi */}
      {accounts.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('finance.account.loadFailed')}
          description={translateError(accounts.error, t)}
        />
      ) : null}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('finance.account.principleTitle')}
        description={t('finance.account.principleBody')}
      />

      <Card>
        <ReportFilterBar
          filter={filter}
          searchPlaceholder={t('finance.account.searchPlaceholder')}
          shown={shown}
          loaded={rows.length}
          extra={
            <>
              <Select
                allowClear
                value={typeFilter?.[0] ?? undefined}
                onChange={(value?: string) => filter.setValues('type', value ? [value] : [])}
                placeholder={t('finance.account.type')}
                style={{ width: 170 }}
                options={ACCOUNT_TYPES.map((value) => ({
                  value,
                  label: t(`accountType.${value}`, { defaultValue: value }),
                }))}
              />
              {/* ⚠️ `allowClear` YO'Q: bo'sh holat «hammasi» degan ma'no
                  berardi, sukut esa — faqat ochiq hisoblar. Yopilganlarni
                  ko'rishning YAGONA yo'li shu tanlagich. */}
              <Select
                value={stateFilter}
                onChange={(value: string) => filter.setValues('state', [value])}
                aria-label={t('finance.account.active')}
                style={{ width: 170 }}
                options={[
                  { value: STATE_OPEN, label: t('finance.account.stateOpen') },
                  { value: STATE_CLOSED, label: t('finance.account.closed') },
                ]}
              />
            </>
          }
        />

        {/* ⚠️ Filtr qo'llanganda daraxt YASSI ro'yxatga aylanadi — sabab
            `matched` izohida. Buni aytmaslik "daraxt buzildi" degan
            taassurot qoldirardi. */}
        {filter.narrowed ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('finance.account.flatWhenFiltered')}
          </Typography.Text>
        ) : null}

        <Table<Account>
          columns={columns}
          dataSource={filter.narrowed ? matched : tree}
          rowKey={(row) => row.id}
          loading={accounts.isPending}
          size="small"
          pagination={false}
          scroll={{ x: 880 }}
          // Chuqurlik cheklanmagan — daraxt o'zi qancha bo'lsa shuncha
          // ochiladi, qavatlar sanalmaydi.
          //
          // ⚠️ Filtr rejimida bu sozlama ZARARSIZ: `matched` qatorlarida
          // `children` maydoni umuman yo'q (ular yassi `Account` lar),
          // ya'ni antd yoyish belgisini chizmaydi. Shart bilan olib
          // tashlash esa jadvalning daraxtligini kod bo'yicha
          // ko'rinmas qilardi.
          expandable={{
            expandedRowKeys,
            onExpand: (expanded, row) =>
              setCollapsed((prev) =>
                expanded ? prev.filter((key) => key !== row.id) : [...prev, row.id],
              ),
          }}
          locale={{
            emptyText: accounts.isError ? (
              <EmptyState variant="error" title={t('finance.account.loadFailed')} />
            ) : rows.length > 0 && visible.length === 0 ? (
              // ⚠️ «Reja bo'sh» va «bu holatda hisob yo'q» ARALASHMAYDI:
              //    «Yopilgan» tanlanganda yopilgan hisob umuman
              //    bo'lmasligi ODATIY hol, va birinchi matn operatorni
              //    mavjud rejani qaytadan qurishga yuborardi.
              <EmptyState
                variant="no-data"
                title={t('finance.account.stateEmpty')}
                description={t('finance.account.stateEmptyHint', { total: rows.length })}
              />
            ) : (
              <ReportTableEmpty
                filter={filter}
                title={t('finance.account.empty')}
                description={t('finance.account.emptyHint')}
              />
            ),
          }}
        />
      </Card>

      <AccountFormModal
        open={formOpen}
        account={editing}
        accounts={rows}
        defaultParentId={parentForNew}
        onClose={() => setFormOpen(false)}
      />
    </>
  )
}
